import {
  Button,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
  toast,
  useDebounceCallback,
} from "@devhop/ui";
import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import { ChevronRight } from "lucide-react";
import {
  type KeyboardEvent,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { usePosWarehousesQuery } from "@/modules/cf-feed/presentation/api/queries";
import type {
  AdminCatalogItem,
  FeedSessionProductGroup,
} from "@/modules/cf-feed/presentation/api/types";
import {
  buildCatalogAddInput,
  isCatalogItemEligibleForAdd,
} from "@/modules/cf-feed/presentation/lib/catalog-add";
import {
  type ProductAllocationDraft,
  parseProductAllocation,
  productAllocationError,
} from "@/modules/cf-feed/presentation/lib/product-allocation";
import { CatalogTable } from "@/modules/cf-feed/presentation/ui/CatalogTable";
import { FeedProductsTable } from "@/modules/cf-feed/presentation/ui/FeedProductsTable";
import { ProductAllocationFields } from "@/modules/cf-feed/presentation/ui/ProductAllocationFields";
import { liveApi } from "@/modules/cf-live/presentation/api/client";
import {
  CATALOG_PAGE_LIMIT,
  useAddDraftProduct,
  useConnectExistingLive,
  useCreateLiveDraft,
  useCreateLiveFromDraft,
  useLiveCapabilitiesQuery,
  useLiveDraftCatalogInfiniteQuery,
  useLiveDraftQuery,
  usePatchDraftProduct,
  usePatchLiveDraft,
  useRemoveDraftProduct,
} from "@/modules/cf-live/presentation/api/queries";
import type { LiveDraftProductItem } from "@/modules/cf-live/presentation/api/types";
import { useFacebookPagesQuery } from "@/modules/facebook/presentation/api/queries";
import { FacebookPageCombobox } from "@/modules/facebook/presentation/ui/FacebookPageCombobox";
import { formatCfApiErrorMessage } from "@/shared/lib/cf-api-error-messages";
import { QueryState } from "@/shared/ui/QueryState";
import { useWorkspaceEnabled } from "../api/workspace-client";
import { LiveWorkspacePage } from "./LiveWorkspacePage";

type LiveCreateSearch = {
  draftId?: string;
  pageId?: string;
};

function groupDraftProducts(
  products: LiveDraftProductItem[],
): FeedSessionProductGroup[] {
  const groups = new Map<string, FeedSessionProductGroup>();
  for (const product of products) {
    const group = groups.get(product.adminProductId) ?? {
      productId: product.adminProductId,
      title: product.name,
      imageUrl: product.imageUrl,
      variants: [],
    };
    const sessionRemaining =
      product.allocationMode === "unlimited" && product.isPreorder
        ? null
        : product.availableToAllocate;
    group.variants.push({
      id: product.id,
      variantId: product.variantId,
      cfCode: product.cfCode,
      title: product.name,
      variantLabel: product.variantLabel ?? product.sku,
      imageUrl: product.imageUrl,
      price: Number(product.price),
      discountPercent: null,
      discountFixedLak: null,
      stockOnHand: product.stockOnHand,
      availableQuantity: sessionRemaining,
      soldQuantity: 0,
      sessionRemaining,
      totalAdded: product.allocatedQuantity ?? 0,
      allocatedQuantity: product.allocatedQuantity,
      allocationMode: product.isPreorder ? product.allocationMode : "limit",
      saleEnabled: product.saleEnabled,
      addedAt: product.addedAt,
      isPreorder: product.isPreorder,
      preorderExpectedDate: product.preorderExpectedDate,
      preorderEndAt: product.preorderEndAt,
      options: [],
    });
    groups.set(product.adminProductId, group);
  }
  return [...groups.values()];
}

export function LiveCreatePage() {
  const search = useSearch({ from: "/app/live/new" });
  const capability = useWorkspaceEnabled();
  if (capability.isLoading) return <p className="p-6">ກຳລັງໂຫຼດ…</p>;
  if (search.draftId && capability.data?.enabled)
    return <LiveWorkspacePage kind="draft" id={search.draftId} />;
  return (
    <LegacyLiveCreatePage
      workspaceEnabled={capability.data?.enabled === true}
    />
  );
}

function LegacyLiveCreatePage({
  workspaceEnabled = false,
}: {
  workspaceEnabled?: boolean;
}) {
  const search = useSearch({ from: "/app/live/new" }) as LiveCreateSearch;
  const nav = useNavigate({ from: "/app/live/new" });
  const createDraft = useCreateLiveDraft();
  const createLive = useCreateLiveFromDraft();
  const connectExisting = useConnectExistingLive();

  const draftId = search.draftId;
  const pageId = search.pageId;

  const draftQuery = useLiveDraftQuery(draftId);
  const capabilitiesQuery = useLiveCapabilitiesQuery(pageId);
  const patchDraft = usePatchLiveDraft(draftId ?? "");
  const addProduct = useAddDraftProduct(draftId ?? "");
  const patchProduct = usePatchDraftProduct(draftId ?? "");
  const removeProduct = useRemoveDraftProduct(draftId ?? "");

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [liveReference, setLiveReference] = useState("");
  const createKeysByDraft = useRef(new Map<string, string>());
  const initializedDraftId = useRef<string | null>(null);
  const [catalogSearchInput, setCatalogSearchInput] = useState("");
  const [catalogQ, setCatalogQ] = useState("");
  const [selectedCatalogIds, setSelectedCatalogIds] = useState<Set<string>>(
    () => new Set(),
  );
  const [selectedProductIds, setSelectedProductIds] = useState<Set<string>>(
    () => new Set(),
  );
  const [isBulkAdding, setIsBulkAdding] = useState(false);
  const [allocationDraft, setAllocationDraft] =
    useState<ProductAllocationDraft>({
      allocationMode: "limit",
      allocatedQuantity: undefined,
    });
  const allocationError = productAllocationError(allocationDraft);
  const addingRef = useRef(false);
  const allocationScope = useRef(draftId);
  useEffect(() => {
    if (allocationScope.current === draftId) return;
    allocationScope.current = draftId;
    setAllocationDraft({
      allocationMode: "limit",
      allocatedQuantity: undefined,
    });
  }, [draftId]);
  const debouncedCatalogSearch = useDebounceCallback((value: string) => {
    setCatalogQ(value.trim());
  }, 350);

  const catalogQuery = useLiveDraftCatalogInfiniteQuery(draftId, {
    q: catalogQ || undefined,
    limit: CATALOG_PAGE_LIMIT,
    groupByProduct: true,
  });

  const pagesQuery = useFacebookPagesQuery();
  const warehousesQuery = usePosWarehousesQuery();
  const activePages =
    pagesQuery.data?.pages.filter(
      (p) => workspaceEnabled || p.status !== "disconnected",
    ) ?? [];

  const products = draftQuery.data?.products ?? [];
  const draftProductGroups = useMemo(
    () => groupDraftProducts(products),
    [products],
  );
  const sessionProductVariantIds = useMemo(
    () => new Set(products.map((p) => p.variantId)),
    [products],
  );
  const catalogItems = useMemo(
    () => catalogQuery.data?.pages.flatMap((page) => page.items) ?? [],
    [catalogQuery.data?.pages],
  );
  const catalogProducts = useMemo(
    () => catalogQuery.data?.pages.flatMap((page) => page.products ?? []) ?? [],
    [catalogQuery.data?.pages],
  );
  const catalogTotal = catalogQuery.data?.pages[0]?.total ?? 0;
  const addableCatalogItems = useMemo(
    () =>
      catalogItems.filter(
        (item) =>
          isCatalogItemEligibleForAdd(item) &&
          !sessionProductVariantIds.has(item.variantId),
      ),
    [catalogItems, sessionProductVariantIds],
  );
  const selectedAddableItems = useMemo(
    () =>
      addableCatalogItems.filter((item) =>
        selectedCatalogIds.has(item.variantId),
      ),
    [addableCatalogItems, selectedCatalogIds],
  );
  const isCatalogAdding = addProduct.isPending || isBulkAdding;

  useEffect(() => {
    const draft = draftQuery.data?.draft;
    if (!draft || initializedDraftId.current === draft.id) return;
    initializedDraftId.current = draft.id;
    setTitle(draft.title);
    setDescription(draft.description ?? "");
  }, [draftQuery.data?.draft]);

  useEffect(() => {
    const draftPageId = draftQuery.data?.draft.pageId;
    if (draftPageId && !pageId) {
      void nav({
        search: { draftId, pageId: draftPageId },
        replace: true,
      });
    }
  }, [draftQuery.data?.draft.pageId, draftId, pageId, nav]);

  useEffect(() => {
    if (!draftId && pageId && !createDraft.isPending) {
      void createDraft.mutateAsync({ pageId }).then(({ draft }) => {
        void nav({
          search: { draftId: draft.id, pageId },
          replace: true,
        });
      });
    }
  }, [draftId, pageId, createDraft, nav]);

  useEffect(() => {
    const ids = new Set(products.map((p) => p.id));
    setSelectedProductIds((prev) => {
      let changed = false;
      const next = new Set<string>();
      for (const id of prev) {
        if (ids.has(id)) next.add(id);
        else changed = true;
      }
      return changed ? next : prev;
    });
  }, [products]);

  useEffect(() => {
    setSelectedCatalogIds((prev) => {
      const validIds = new Set(
        catalogItems
          .filter(
            (item) =>
              isCatalogItemEligibleForAdd(item) &&
              !sessionProductVariantIds.has(item.variantId),
          )
          .map((item) => item.variantId),
      );
      let changed = false;
      const next = new Set<string>();
      for (const id of prev) {
        if (validIds.has(id)) next.add(id);
        else changed = true;
      }
      return changed ? next : prev;
    });
  }, [catalogItems, sessionProductVariantIds]);

  const setPage = (id: string) => {
    setSelectedCatalogIds(new Set());
    setLiveReference("");
    initializedDraftId.current = null;
    nav({
      search: { pageId: id, draftId: undefined },
    });
  };

  const handleAdd = async (item: AdminCatalogItem) => {
    if (addingRef.current) return;
    if (!draftId) {
      toast.error("ກະລຸນາເລືອກເພຈກ່ອນ");
      return;
    }
    if (allocationError) {
      toast.error(formatCfApiErrorMessage(allocationError));
      return;
    }
    addingRef.current = true;
    try {
      await addProduct.mutateAsync({
        ...buildCatalogAddInput(item),
        ...parseProductAllocation(allocationDraft),
      });
    } catch {
      return;
    } finally {
      addingRef.current = false;
    }
  };

  const handleCatalogSearchKeyDown = async (
    event: KeyboardEvent<HTMLInputElement>,
  ) => {
    if (event.key !== "Enter") return;
    const code = event.currentTarget.value.trim();
    if (!code || !draftId) return;
    event.preventDefault();
    try {
      const result = await liveApi.draftCatalog(draftId, {
        q: code,
        limit: 20,
        offset: 0,
      });
      const matches = result.items.filter(
        (item) =>
          item.sku.toLowerCase() === code.toLowerCase() ||
          item.barcodePrefix?.toLowerCase() === code.toLowerCase(),
      );
      const onlyMatch = matches.length === 1 ? matches[0] : undefined;
      if (onlyMatch) {
        await handleAdd(onlyMatch);
        setCatalogSearchInput("");
        setCatalogQ("");
        return;
      }
      setCatalogSearchInput(code);
      setCatalogQ(code);
      toast.info(
        matches.length > 1
          ? `ພົບ ${matches.length} variant — ເລືອກຈາກລາຍການ`
          : "ບໍ່ພົບ barcode ທີ່ກົງກັນ",
      );
    } catch {
      toast.error("ຄົ້ນຫາ barcode ລົ້ມເຫຼວ");
    }
  };

  const handleBulkAdd = async () => {
    if (addingRef.current || allocationError) return;
    if (!draftId) {
      toast.error("ກະລຸນາເລືອກເພຈກ່ອນ");
      return;
    }
    if (selectedAddableItems.length === 0) {
      toast.error("ກະລຸນາເລືອກສິນຄ້າກ່ອນ");
      return;
    }

    addingRef.current = true;
    setIsBulkAdding(true);
    try {
      const result = await liveApi.addDraftProductsBySelection(
        draftId,
        {
          mode: "selected",
          variantIds: selectedAddableItems.map((item) => item.variantId),
        },
        parseProductAllocation(allocationDraft),
      );
      if (result.added.length > 0) {
        await Promise.all([draftQuery.refetch(), catalogQuery.refetch()]);
      }
      setSelectedCatalogIds(
        new Set(result.failed.map((failure) => failure.variantId)),
      );
      if (result.failed.length > 0) {
        toast.warning(
          `ເພີ່ມສຳເລັດ ${result.added.length} ລາຍການ · ບໍ່ສຳເລັດ ${result.failed.length} ລາຍການ`,
        );
      }
    } catch (error) {
      toast.error("ເພີ່ມສິນຄ້າບໍ່ສຳເລັດ", {
        description: error instanceof Error ? error.message : undefined,
      });
    } finally {
      addingRef.current = false;
      setIsBulkAdding(false);
    }
  };

  const handleAddAllCatalog = async () => {
    if (addingRef.current || allocationError) return;
    if (!draftId) {
      toast.error("ກະລຸນາເລືອກເພຈກ່ອນ");
      return;
    }

    addingRef.current = true;
    setIsBulkAdding(true);
    try {
      const result = await liveApi.addDraftProductsBySelection(
        draftId,
        {
          mode: "all_matching",
          q: catalogQ || undefined,
        },
        parseProductAllocation(allocationDraft),
      );
      if (result.added.length > 0) {
        await Promise.all([draftQuery.refetch(), catalogQuery.refetch()]);
      }
      if (result.failed.length > 0) {
        toast.warning(
          `ເພີ່ມສຳເລັດ ${result.added.length} ລາຍການ · ບໍ່ສຳເລັດ ${result.failed.length} ລາຍການ`,
        );
      } else if (result.added.length > 0) {
        toast.success(`ເພີ່ມສິນຄ້າ ${result.added.length} ລາຍການແລ້ວ`);
      } else {
        toast.info("ບໍ່ມີສິນຄ້າໃໝ່ທີ່ສາມາດເພີ່ມໄດ້");
      }
    } catch (error) {
      toast.error("ເພີ່ມສິນຄ້າບໍ່ສຳເລັດ", {
        description: error instanceof Error ? error.message : undefined,
      });
    } finally {
      addingRef.current = false;
      setIsBulkAdding(false);
    }
  };

  const draftWarehouseId =
    draftQuery.data?.draft.retailStockWarehouseId ?? undefined;
  const warehouses = warehousesQuery.data?.items ?? [];

  const handleBulkRemove = async (targetIds = [...selectedProductIds]) => {
    if (targetIds.length === 0) {
      toast.error("ກະລຸນາເລືອກສິນຄ້າກ່ອນ");
      return;
    }

    for (const id of targetIds) {
      try {
        await removeProduct.mutateAsync(id);
      } catch {
        break;
      }
    }
    setSelectedProductIds(new Set());
  };

  const handleCreateLive = async () => {
    if (!draftId) {
      toast.error("ກະລຸນາເລືອກເພຈກ່ອນ");
      return;
    }
    if (!title.trim()) {
      toast.error("ກະລຸນາໃສ່ຊື່ Live");
      return;
    }
    if (products.length === 0) {
      toast.error("ກະລຸນາເພີ່ມສິນຄ້າກ່ອນ");
      return;
    }

    try {
      await patchDraft.mutateAsync({
        title,
        description: description.trim() || null,
      });
      let idempotencyKey = createKeysByDraft.current.get(draftId);
      if (!idempotencyKey) {
        idempotencyKey = crypto.randomUUID();
        createKeysByDraft.current.set(draftId, idempotencyKey);
      }
      const result = await createLive.mutateAsync({
        draftId,
        idempotencyKey,
      });
      await nav({ to: "/app/live/$id", params: { id: result.feedId } });
    } catch {
      return;
    }
  };

  const handleConnectExisting = async () => {
    if (!draftId || !liveReference.trim()) return;
    try {
      await patchDraft.mutateAsync({
        title,
        description: description.trim() || null,
      });
      const result = await connectExisting.mutateAsync({
        draftId,
        liveReference: liveReference.trim(),
      });
      await nav({ to: "/app/live/$id", params: { id: result.feedId } });
    } catch {
      return;
    }
  };

  const selectedPage = activePages.find((p) => p.id === pageId);
  const pageTokenBad =
    selectedPage?.tokenStatus === "expired" ||
    selectedPage?.tokenStatus === "invalid" ||
    selectedPage?.tokenStatus === "revoked";
  const capabilities = capabilitiesQuery.data;

  return (
    <>
      <Header />
      <Main>
        <nav className="mb-4 flex flex-wrap items-center gap-1 text-muted-foreground text-sm">
          <Link to="/app/live" className="hover:text-foreground">
            Live
          </Link>
          <ChevronRight className="size-4" />
          <span className="text-foreground">ສ້າງ Live</span>
        </nav>

        <div className="mb-6 flex flex-col gap-4 rounded-xl border bg-card p-4 lg:flex-row lg:items-end lg:justify-between">
          <div className="flex flex-1 flex-col gap-4 sm:flex-row sm:flex-wrap">
            <div className="min-w-[200px] space-y-1">
              <span className="text-muted-foreground text-xs">ເພຈ</span>
              <FacebookPageCombobox
                pages={activePages}
                value={pageId ?? ""}
                onValueChange={setPage}
                disabled={createDraft.isPending}
                placeholder="ກະລຸນາເລືອກເພຈກ່ອນ"
                queryKey={["cf-live-create-pages"]}
              />
            </div>

            <div className="min-w-[220px] space-y-1">
              <span className="text-muted-foreground text-xs">ຄັງສິນຄ້າ</span>
              <Select
                value={draftWarehouseId ?? ""}
                onValueChange={(id) => {
                  if (draftId && id) {
                    patchDraft.mutate({ warehouseId: id });
                  }
                }}
                disabled={!draftId || patchDraft.isPending}
              >
                <SelectTrigger>
                  <SelectValue placeholder="ເລືອກຄັງ" />
                </SelectTrigger>
                <SelectContent>
                  {warehouses.map((w) => (
                    <SelectItem key={w.id} value={w.id}>
                      {w.name}
                      {w.isMainStock ? " (ຫຼັກ)" : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="min-w-[200px] space-y-1">
              <Label
                htmlFor="live-title"
                className="text-muted-foreground text-xs"
              >
                ຊື່ Live
              </Label>
              <Input
                id="live-title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="ຊື່ Live"
                disabled={!draftId}
              />
            </div>

            <div className="min-w-[240px] flex-1 space-y-1">
              <Label
                htmlFor="live-desc"
                className="text-muted-foreground text-xs"
              >
                ລາຍລະອຽດ Live
              </Label>
              <Textarea
                id="live-desc"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={2}
                disabled={!draftId}
              />
            </div>
          </div>

          <Button
            type="button"
            disabled={
              !draftId ||
              createLive.isPending ||
              connectExisting.isPending ||
              patchDraft.isPending ||
              products.length === 0 ||
              !title.trim()
            }
            onClick={() => void handleCreateLive()}
          >
            ສ້າງ Live
          </Button>
        </div>

        {pageTokenBad ? (
          <p className="mb-4 text-destructive text-sm">
            ກະລຸນາເຊື່ອມຕໍ່ Facebook ໃໝ່
            {" · "}
            <Link
              to="/app/facebook"
              search={{
                facebook: undefined,
                reason: undefined,
                pages: undefined,
                missing: undefined,
                omitted: undefined,
              }}
              className="underline"
            >
              ຈັດການເຊື່ອມ Facebook
            </Link>
          </p>
        ) : null}

        {pageId ? (
          <div className="mb-4 rounded-lg border bg-card p-3 text-sm">
            {capabilitiesQuery.isLoading ? (
              <p className="text-muted-foreground">
                ກຳລັງກວດສິດສ້າງ Facebook Live...
              </p>
            ) : capabilities?.canCreateLive ? (
              <p className="text-emerald-600 dark:text-emerald-400">
                Facebook Page ພ້ອມສ້າງ Live
              </p>
            ) : (
              <div className="space-y-1">
                <p className="font-medium text-amber-600 dark:text-amber-400">
                  Facebook ອາດຍັງບໍ່ອະນຸຍາດສ້າງ Live
                </p>
                {capabilities?.missingPermissions.length ? (
                  <p className="text-muted-foreground text-xs">
                    ສິດທີ່ຂາດ: {capabilities.missingPermissions.join(", ")}
                  </p>
                ) : null}
                <p className="text-muted-foreground text-xs">
                  ຍັງສາມາດກົດສ້າງໄດ້; ຖ້າ Meta ປະຕິເສດ ຮ່າງ Live ແລະສິນຄ້າຈະບໍ່ຖືກລຶບ
                </p>
              </div>
            )}
          </div>
        ) : null}

        {draftId ? (
          <div className="mb-4 flex flex-col gap-3 rounded-lg border bg-card p-3 sm:flex-row sm:items-end">
            <div className="min-w-0 flex-1 space-y-1">
              <Label htmlFor="existing-live-reference">
                ເຊື່ອມ Facebook Live ທີ່ສ້າງໄວ້ແລ້ວ
              </Label>
              <Input
                id="existing-live-reference"
                value={liveReference}
                onChange={(event) => setLiveReference(event.target.value)}
                placeholder="Facebook Live URL ຫຼື Video ID"
                disabled={connectExisting.isPending || createLive.isPending}
              />
              <p className="text-muted-foreground text-xs">
                ຖ້າບໍ່ໃສ່ຊື່ ລະບົບຈະໃຊ້ຊື່ຈາກ Facebook Live ອັດຕະໂນມັດ
              </p>
            </div>
            <Button
              type="button"
              variant="outline"
              disabled={
                !liveReference.trim() ||
                connectExisting.isPending ||
                createLive.isPending ||
                patchDraft.isPending
              }
              onClick={() => void handleConnectExisting()}
            >
              {connectExisting.isPending ? "ກຳລັງເຊື່ອມ..." : "ເຊື່ອມ Live"}
            </Button>
          </div>
        ) : null}

        {selectedPage ? (
          <p className="mb-4 text-muted-foreground text-sm">
            ເພຈ:{" "}
            <strong className="text-foreground">{selectedPage.pageName}</strong>
            {draftId ? (
              <span className="ml-3 text-xs">ຮ່າງ Live ກຳລັງຕັ້ງຄ່າ</span>
            ) : null}
          </p>
        ) : null}

        <div className="space-y-4">
          <section className="rounded-xl border bg-card">
            <div className="flex flex-col gap-3 border-b p-4">
              <div>
                <h2 className="font-medium">ລາຍການສິນຄ້າ ({catalogTotal})</h2>
                <p className="text-muted-foreground text-xs">
                  ເລືອກແລ້ວ {selectedAddableItems.length}
                </p>
              </div>
              <ProductAllocationFields
                value={allocationDraft}
                onChange={setAllocationDraft}
                disabled={!draftId || isCatalogAdding}
              />
              <p className="text-muted-foreground text-xs">
                ໃຊ້ກັບສິນຄ້າທີ່ເພີ່ມໃໝ່ · ເພີ່ມທີ່ເລືອກ/ທັງໝົດໃຊ້ໂໝດນີ້ຕາມສິດ preorder ຈາກ POS
              </p>
              <div className="flex flex-wrap items-center gap-2">
                <Button
                  type="button"
                  variant="outline"
                  disabled={
                    !draftId ||
                    isCatalogAdding ||
                    Boolean(allocationError) ||
                    catalogTotal === 0
                  }
                  onClick={() => void handleAddAllCatalog()}
                >
                  {isBulkAdding ? "ກຳລັງເພີ່ມ..." : "ເພີ່ມທັງໝົດ"}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  disabled={
                    !draftId ||
                    Boolean(allocationError) ||
                    isCatalogAdding ||
                    selectedAddableItems.length === 0
                  }
                  onClick={() => void handleBulkAdd()}
                >
                  {isBulkAdding ? "ກຳລັງເພີ່ມ..." : "ເພີ່ມທີ່ເລືອກ"}
                </Button>
                <Input
                  placeholder="ຄົ້ນຫາສິນຄ້າ / SKU / ສະແກນ barcode"
                  value={catalogSearchInput}
                  onChange={(e) => {
                    setCatalogSearchInput(e.target.value);
                    debouncedCatalogSearch(e.target.value);
                  }}
                  onKeyDown={(event) => void handleCatalogSearchKeyDown(event)}
                  className="min-w-[220px] flex-1 sm:max-w-xs"
                  disabled={!draftId}
                />
              </div>
            </div>
            {!draftId ? (
              <div className="flex flex-col items-center justify-center gap-2 p-12 text-center text-muted-foreground">
                <p className="text-sm">ເພີ່ມສິນຄ້າເຂົ້າ Live</p>
                <p className="text-xs">ເລືອກເພຈກ່ອນ</p>
              </div>
            ) : (
              <QueryState
                result={catalogQuery}
                isFetching={
                  catalogQuery.isFetching && !catalogQuery.isFetchingNextPage
                }
              >
                <div className="overflow-x-auto">
                  <CatalogTable
                    data={catalogItems}
                    isLoading={catalogQuery.isLoading}
                    offset={0}
                    limit={CATALOG_PAGE_LIMIT}
                    total={catalogTotal}
                    products={catalogProducts}
                    inPostVariantIds={sessionProductVariantIds}
                    hasFeed={Boolean(draftId)}
                    isAdding={isCatalogAdding}
                    selectedIds={selectedCatalogIds}
                    onSelectionChange={setSelectedCatalogIds}
                    onPaginationChange={() => undefined}
                    hasNextPage={catalogQuery.hasNextPage}
                    isFetchingNextPage={catalogQuery.isFetchingNextPage}
                    onLoadMore={() => {
                      if (
                        catalogQuery.hasNextPage &&
                        !catalogQuery.isFetchingNextPage
                      ) {
                        void catalogQuery.fetchNextPage();
                      }
                    }}
                    onAdd={(item) => void handleAdd(item)}
                  />
                </div>
              </QueryState>
            )}
          </section>

          <section className="rounded-xl border bg-card">
            <div className="border-b p-4">
              <div>
                <h2 className="font-medium">ສິນຄ້າໃນ Live ({products.length})</h2>
                <p className="text-muted-foreground text-xs">
                  ເລືອກແລ້ວ {selectedProductIds.size}
                </p>
              </div>
            </div>
            {!draftId ? (
              <div className="flex flex-col items-center justify-center gap-2 p-12 text-center text-muted-foreground">
                <p className="text-sm">ເພີ່ມສິນຄ້າເຂົ້າ Live</p>
                <p className="text-xs">ເລືອກເພຈກ່ອນ</p>
              </div>
            ) : (
              <QueryState result={draftQuery}>
                <div className="p-3">
                  <FeedProductsTable
                    products={draftProductGroups}
                    isLoading={draftQuery.isFetching}
                    selectedIds={selectedProductIds}
                    onSelectionChange={setSelectedProductIds}
                    onBulkRemove={(ids) => void handleBulkRemove(ids)}
                    onToggleSale={(id, enabled) =>
                      patchProduct.mutate({
                        productId: id,
                        body: { saleEnabled: enabled },
                      })
                    }
                    onSaveAllocation={(productId, body) =>
                      patchProduct.mutateAsync({ productId, body })
                    }
                    onRemove={(id) => removeProduct.mutate(id)}
                    isSaving={patchProduct.isPending}
                    isRemoving={removeProduct.isPending}
                  />
                </div>
              </QueryState>
            )}
          </section>
        </div>
      </Main>
    </>
  );
}
